"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE } from "@/constant";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectSearchAddressResult, setSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { resetSearch, markSearch, setSearchQuery } from "stores/search/searchSlice";
import { SortByType } from "stores/search/types";
import { canUseDOM } from "utils/dom";
import { extractBoolean, extractInt, getDescription, getTitle } from "utils/helper";
import { dashedToCommaSeparatedName } from "utils/url";
import { PropsType } from "./type";
export const SetSearchStoreLogic = ({
  search
}: {
  search: PropsType["search"];
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const searchAddress = useAppSelector(selectSearchAddressResult);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (canUseDOM() && searchParams) {
      const FeaturesAndAmenitiesIds = searchParams.get("FeaturesAndAmenities") ? searchParams.get("FeaturesAndAmenities")?.split(",")?.map(Number) : [];
      const LocationIds = searchParams.get("Location") ? searchParams.get("Location")?.split(",")?.map(Number) : [];
      const propertyTypeDescValues = searchParams.get("propertyTypeDesc") ? searchParams.get("propertyTypeDesc")?.split(",") : [];
      const allowSameDay = extractBoolean(searchParams, "allowSameDay");
      dispatch(resetSearch());
      dispatch(markSearch({
        page: extractInt(searchParams, "page"),
        lat: parseFloat(searchParams.get("lat") || "0") || search.lat || 37.7749295,
        lng: parseFloat(searchParams.get("lng") || "0") || search.lng || -122.4194155,
        bounds: searchParams.get("bounds") || search.bounds || null,
        shortName: dashedToCommaSeparatedName(searchParams.get("name") || search.shortName || "USA"),
        longName: dashedToCommaSeparatedName(searchParams.get("placeDesc") || search.longName || ""),
        placeId: searchParams.get("placeId") || search.placeId || "ChIJCzYy5IS16lQRQrfeQ5K5Oxw",
        placeType: searchParams.get("placeType") || search.placeType?.[0] || "",
        startDate: searchParams.get("startDate") || "" || null,
        endDate: searchParams.get("endDate") || "" || null,
        noOfDays: extractInt(searchParams, "noOfDays"),
        adultCount: extractInt(searchParams, "adultCount", "2"),
        childrenCount: extractInt(searchParams, "childrenCount"),
        petAllowed: extractBoolean(searchParams, "petAllowed"),
        isSearchingInMap: extractBoolean(searchParams, "isSearchingInMap"),
        numberOfGuest: extractInt(searchParams, "adultCount", "2") + extractInt(searchParams, "childrenCount"),
        numberOfBathroom: extractInt(searchParams, "minBathRoom"),
        numberOfBedroom: extractInt(searchParams, "minBedRoom"),
        minBed: extractInt(searchParams, "minBed"),
        minPrice: extractInt(searchParams, "minPrice"),
        maxprice: extractInt(searchParams, "maxprice"),
        FeaturesAndAmenities: FeaturesAndAmenitiesIds,
        Location: LocationIds,
        propertyTypeDesc: propertyTypeDescValues,
        smokeAllowed: extractBoolean(searchParams, "smokeAllowed"),
        childAllowed: extractBoolean(searchParams, "childAllowed") || extractBoolean(searchParams, "suitableInfant") || extractBoolean(searchParams, "suitableChild"),
        eventAllowed: extractBoolean(searchParams, "eventAllowed"),
        toggleOn: extractBoolean(searchParams, "toggleOn", "true") ?? true,
        sortBy: (searchParams.get("sortBy") as SortByType) || "highToLow",
        propertyConfigTypeId: extractInt(searchParams, "propertyConfigTypeId") || null,
        isBoundaryVisible: searchParams.get("isBoundaryVisible") ? extractBoolean(searchParams, "isBoundaryVisible") : true,
        isBoundaryAutoRemoved: searchParams.get("isBoundaryAutoRemoved") ? extractBoolean(searchParams, "isBoundaryAutoRemoved") : false,
        cancellable: searchParams.get("cancellable") ? extractBoolean(searchParams, "cancellable") : false,
        nearByProperties: searchParams.get("nearByProperties") ? extractBoolean(searchParams, "nearByProperties") : false,
        limit: extractInt(searchParams, "limit") || NO_OF_PROERTIES_PER_PAGE_IN_LISTING_PAGE,
        allowSameDay: allowSameDay
      }));

      // do not update the URL at initial render
      void dispatch(setSearchQuery({
        shouldUpdateUrl: false
      }));
    }

    // called when  component unmount [when user will go to another page]
    return () => {
      // "clean up store"
      dispatch(resetSearch());
    };
  }, [dispatch, search, searchParams, pathname]);
  useEffect(() => {
    // Dispatch setSearchAddressResult action with searchedPlace
    if (searchAddress === null || searchAddress?.address_components?.length === 0) {
      dispatch(setSearchAddressResult({
        address_components: search?.searchedPlace?.address_components
      }));
    }
  }, [dispatch, search?.searchedPlace?.address_components, searchAddress]);

  // dynamically update page title and meta description based on search parameters
  useEffect(() => {
    const locationName = dashedToCommaSeparatedName(searchParams?.get("name") || search.shortName || "USA");

    // Update title and description based on current location or search criteria
    const title = getTitle(locationName, true);
    const description = getDescription(locationName, true);
    document.title = title; // Update the document title

    const metaDescription = document.querySelector('meta[name="description"]'); // Get the meta description element

    // Change the content attribute
    if (metaDescription) {
      metaDescription.setAttribute("content", description);
    }
  }, [searchParams, search.shortName]);
  return null;
};